import { createDate, isAfter } from '@lingoda/dates';
import { trans, transChoice } from '@lingoda/i18n';
import { isPointsChallengeTextsEnforced } from '@lingoda/local-feature-flags';
import type { Date } from '@lingoda/dates';
import hiddenPrizeIcon from '../assets/icons/hidden-prize-200.svg?url';

// Badge notifications created from October 2026 on belong to points challenges, so they get the
// points copy. The payload itself carries no unit for prizeValue, so the creation date is the
// closest available signal; a legacy-challenge badge committed after this date (e.g. a September
// activity event consumed past midnight) shows the new copy — accepted trade-off.
const POINTS_COPY_FROM = '2026-10-01';

interface Params {
    challengeName: string;
    // The achieved badge's threshold: points on points challenges, activity count on legacy ones
    prizeValue: number;
    createdAt: Date;
}

export const getChallengePrizeAchievedNotificationContent = ({
    challengeName,
    prizeValue,
    createdAt,
}: Params) => {
    // createdAt comes from parseStudentNotification via createDate, so the cutoff must be parsed
    // with createDate too — both sides in the same timezone frame. QA can force the points copy
    // via the same dev flag that forces it on the challenge pages.
    const isPointsChallenge =
        isPointsChallengeTextsEnforced() || isAfter(createdAt, createDate(POINTS_COPY_FROM));

    const description = isPointsChallenge
        ? trans(
              'activity-challenge-notification-description-points',
              { points: prizeValue },
              'student-challenges',
          )
        : transChoice(
              'activity-challenge-notification-description',
              prizeValue,
              { activitiesCount: prizeValue },
              'student-challenges',
          );

    const buttonLabel = isPointsChallenge
        ? trans('btn-view-badge', {}, 'student-challenges')
        : trans('btn-see-prize', {}, 'student-challenges');

    return {
        header: getLegacyChallengeHeader(challengeName),
        description,
        buttonLabel,
        // Untranslated, for analytics — must match what the button says, not what it said in 2024
        trackingSection: isPointsChallenge ? 'View badge' : 'See prize',
        iconUrl: hiddenPrizeIcon,
    };
};

const getLegacyChallengeHeader = (challengeName: string) => {
    switch (challengeName) {
        case 'may-2024':
            return trans('may-2024-title', {}, 'student-challenges');
        case 'april-2024':
            return trans('april-2024-title', {}, 'student-challenges');
        case 'march-2024':
            return trans('march-2024-title', {}, 'student-challenges');
        case 'february-2024':
            return trans('february-2024-title', {}, 'student-challenges');
        case 'january-2024':
            return trans('january-2024-title', {}, 'student-challenges');
        case 'december-2023':
            return trans('december-2023-title', {}, 'student-challenges');
        default:
            return '';
    }
};
